import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Slide } from '@material-ui/core';
import { useLocalStorage } from 'react-use';
import { connect } from 'react-redux';

import { myProfile } from 'Redux/user-profile/selectors';
import {
  SvgIcon,
  ButtonFilled,
  ButtonOutlined,
  Box,
  Text,
  MUIModal,
  Formik,
  Flex,
} from 'Components/Base';
import { useFaqTracking } from 'Util/Analytics/useFaqTracking';
import { AttachmentFeedbackRating } from 'Components/Classroom/Clear';
import { REPORT_STATE } from 'Containers/Feedback/constant';

import ClearButton from '../ClearButton';
import { SUBMISSION_STATE, useClearByOnDemand } from '../../Contexts/ClearByOnDemandProvider';

const { FormContainer, Form, FormikInput } = Formik;

const Transition = React.forwardRef((props, ref) => <Slide direction="up" ref={ref} {...props} />);
Transition.displayName = 'Transition';

function FeedbackModalContent({ feedbackState, onSubmitForm, attachment }) {
  const [submissionState, , , ticket] = useClearByOnDemand();
  const { logOpenClear, logSubmittedClearTicket } = useFaqTracking();

  useEffect(() => {
    if (submissionState === SUBMISSION_STATE.COMPLETED && ticket?.ticketId) {
      logSubmittedClearTicket({
        faqId: attachment.id,
        faqTitle: attachment.title,
        ticketId: ticket.ticketId,
      });
    }
  }, [submissionState]);

  if (feedbackState === REPORT_STATE.FILLING_FORM) {
    return (
      <Box width={1}>
        <Text textStyle="heading5" pb={6}>
          โปรดระบุเหตุผลที่ไม่เข้าใจคำอธิบายนี้
        </Text>
        <FormContainer onSubmit={onSubmitForm} initialValues={{ feedback: '' }}>
          <Form id="feedback-like-form">
            <FormikInput.TextArea
              name="feedback"
              rows={7}
              rowsMax={7}
              placeholder="บอกเหตุผลที่ไม่เข้าใจให้ฟังหน่อย (ถ้ามี)"
            />
            <Flex
              mt={4}
              justifyContent="flex-end"
              flexWrap={{ _: 'wrap', sm: 'nowrap' }}
              style={{ gap: 16 }}
            >
              <ClearButton
                withTooltip={false}
                renderButton={(onClick) => (
                  <ButtonOutlined
                    type="submit"
                    fontWeight={600}
                    color="clear.clearDark"
                    width={{ _: 1, sm: 'auto' }}
                    onClick={() => {
                      logOpenClear({
                        faqId: attachment.id,
                        faqTitle: attachment.title,
                      });
                      onClick();
                    }}
                  >
                    ส่งความเห็นและถาม CLEAR
                  </ButtonOutlined>
                )}
              />

              <ButtonFilled type="submit" color="clear.clearDark" width={{ _: 1, sm: 'auto' }}>
                ส่งความเห็น
              </ButtonFilled>
            </Flex>
          </Form>
        </FormContainer>
      </Box>
    );
  }

  return null;
}

function AttachmentFeedback({ attachment, userId }) {
  const [feedbackState, setFeedbackState] = useState(REPORT_STATE.INITIALIZED);
  const selectedRatingRef = useRef(null);

  const [faqRatingsStore, setFaqRatingsStore] = useLocalStorage(`${userId}_rating_faq`, []);

  const { logVoteItem } = useFaqTracking();

  const options = [
    { icon: SvgIcon.ICON_LIST.FACE_LAUGH, label: 'เข้าใจมาก', value: 4 },
    { icon: SvgIcon.ICON_LIST.FACE_SMILE, label: 'เข้าใจ', value: 3 },
    { icon: SvgIcon.ICON_LIST.FACE_FROWN, label: 'ไม่เข้าใจ', value: 2 },
    { icon: SvgIcon.ICON_LIST.FACE_FROWN_OPEN, label: 'สับสนเพิ่ม', value: 1 },
  ];

  const storedRating = faqRatingsStore[attachment.id] || null;

  const updateFaqRatingsStore = (newRating) => {
    setFaqRatingsStore((prevRatings) => ({
      ...prevRatings,
      [attachment.id]: newRating,
    }));
  };

  const submitForm = (formValue) => {
    logVoteItem({
      faqId: attachment.id,
      faqTitle: attachment.title,
      rating: selectedRatingRef.current,
      ratingDetail: formValue.feedback,
    });
    setFeedbackState(REPORT_STATE.INITIALIZED);
    updateFaqRatingsStore(selectedRatingRef.current);
  };

  const closeFormModal = () => {
    if (feedbackState === REPORT_STATE.FILLING_FORM) {
      logVoteItem({
        faqId: attachment.id,
        faqTitle: attachment.title,
        rating: selectedRatingRef.current,
      });
      setFeedbackState(REPORT_STATE.INITIALIZED);
      updateFaqRatingsStore(selectedRatingRef.current);
    } else {
      setFeedbackState(REPORT_STATE.INITIALIZED);
    }
  };

  const clickVoteItem = (rating) => {
    if (rating > 2) {
      logVoteItem({
        faqId: attachment.id,
        faqTitle: attachment.title,
        rating,
      });
      setFeedbackState(REPORT_STATE.INITIALIZED);
      updateFaqRatingsStore(rating);
    } else {
      selectedRatingRef.current = rating;
      setFeedbackState(REPORT_STATE.FILLING_FORM);
    }
  };

  if (!attachment) return null;

  return (
    <>
      <AttachmentFeedbackRating
        options={options}
        onClickOption={clickVoteItem}
        mt={8}
        selectedRating={storedRating}
      />

      <MUIModal
        TransitionComponent={Transition}
        isControlled
        isOpen={feedbackState !== REPORT_STATE.INITIALIZED}
        onCloseModal={closeFormModal}
        innerProps={{ height: 'auto', display: 'flex', flex: '1' }}
        modalTitleProps={{ disableTypography: true }}
        modalStyle={{ maxWidth: '480px' }}
      >
        <FeedbackModalContent
          feedbackState={feedbackState}
          onSubmitForm={submitForm}
          attachment={attachment}
        />
      </MUIModal>
    </>
  );
}

AttachmentFeedback.propTypes = {
  attachment: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
  }),
};

FeedbackModalContent.propTypes = {
  feedbackState: PropTypes.string,
  onSubmitForm: PropTypes.func,
  attachment: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
  }),
};

export default connect((state) => {
  const user = myProfile(state);
  return {
    userId: user.id,
  };
}, {})(AttachmentFeedback);
